import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button, Card, Heading, HorizontalDescriptionList } from 'yoso';
import { BaseCard } from '~/components/Card';
import { Checkbox } from '~/components/Form';
import { Translation } from '~/components/I18n/Translation';
import { LandZone, useLand } from '~/components/Land/Land.hooks';
import { zoneImageMap } from '~/components/Land/Land.meta';
import { useParticipationBottomSheet } from '~/components/Land/Participation/LandParticipation.hooks';
import { Lup } from '~/components/Lup/Lup';
import { useMember } from '~/components/Member/Member.hooks';
import { Pair } from '~/components/Pair';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { format } from '~/components/Util/number';
import { isVaultIosDevice } from '~/components/Util/ua';
import styles from './ZoneCard.module.scss';

const buttonXltByStatus = {
  PARTICIPATED: <Translation name="citizen_button_title_land_joined" />,
  NOT_PARTICIPATABLE: <Translation name="citizen_button_title_cannot_join" />,
};

export type ZoneCardProps = LandZone & {
  landId: number;
};

export const ZoneCard = ({ landId, ...zone }: ZoneCardProps) => {
  const {
    data: { isLoggedIn },
  } = useMember();
  const {
    zoneId,
    requiredLevelUpPassCount,
    isParticipatable,
    zoneNumber,
    rewardCurrency,
    requiredAsset,
    rewardAmount,
    participationStatus,
  } = zone;
  const {
    data: { isParticipated, citizenNftLevel, myHoldings, landStatus, myParticipation },
  } = useLand(landId);
  const { levelUpPassCount, isHolderOfCitizenNft } = myHoldings;

  const { mutateAsync, isLoading } = useParticipationBottomSheet({ landId, zoneId });
  const onClick = useCallback(() => mutateAsync(), [mutateAsync]);

  const ImageComponent = zoneImageMap[`level${citizenNftLevel}_zone${zoneNumber}`] || 'img';

  const achieveLupRequirement = levelUpPassCount >= requiredLevelUpPassCount;

  const error = useMemo(() => {
    if (isParticipated && myParticipation?.zoneId === zoneId) return 'PARTICIPATED';
    if (!isHolderOfCitizenNft || !achieveLupRequirement || !isParticipatable) {
      return 'NOT_PARTICIPATABLE';
    }
  }, [
    achieveLupRequirement,
    isHolderOfCitizenNft,
    isParticipatable,
    isParticipated,
    myParticipation?.zoneId,
    zoneId,
  ]);

  const isZoneParticipated = myParticipation?.zoneId === zoneId;

  return (
    <BaseCard className={classNames(styles.root, styles[landStatus])} shadow size="large">
      <div className={styles.header}>
        <Tooltip
          variant="secondary"
          tooltipText={<Translation name="citizen_land_detail_zonelist_joining_desc" />}
          active={isZoneParticipated}
          className={styles.tooltip}>
          <ImageComponent
            className={styles.image}
            alt={`level${citizenNftLevel}_zone${zoneNumber}`}
            width={130}
            height={130}
          />
        </Tooltip>
        <Heading className={styles.title}>
          <Translation name="citizen_land_zone" /> {zoneNumber}
        </Heading>
        <Pair
          className={styles.pair}
          primaryPrice={rewardAmount}
          primaryCurrency={rewardCurrency}
          secondaryCurrency="USD"
        />
      </div>
      <div className={styles.body}>
        <Card className={styles.condition}>
          <Heading level={5} className={styles.conditionTitle}>
            <Translation name="citizen_land_detail_guide_banner1_title" />
          </Heading>
          <HorizontalDescriptionList className={styles.descriptionList}>
            <dt>Citizen</dt>
            <dd>
              {`Lv${format(citizenNftLevel, { zeroAsEmpty: true })}`}{' '}
              <Checkbox
                className={styles.checkbox}
                variant="circle"
                checked={isHolderOfCitizenNft}
                readOnly
              />
            </dd>
            {requiredAsset.amount > 0 && (
              <>
                <dt>DON</dt>
                <dd>
                  {format(requiredAsset.amount, { zeroAsEmpty: true })} {requiredAsset.assetType}
                  <Checkbox
                    className={styles.checkbox}
                    variant="circle"
                    checked={isHolderOfCitizenNft}
                    readOnly
                  />
                </dd>
              </>
            )}
            <dt>LUP</dt>
            <dd>
              <Lup value={requiredLevelUpPassCount} zeroAsEmpty={true} />
              <Checkbox
                className={styles.checkbox}
                variant="circle"
                checked={achieveLupRequirement}
                readOnly
              />
            </dd>
          </HorizontalDescriptionList>
        </Card>
        <Card className={styles.status}>
          <Heading level={5} className={styles.statusTitle}>
            <Translation name="citizen_land_detail_status_title" />
          </Heading>
          <HorizontalDescriptionList className={styles.descriptionList}>
            <dt>
              <Translation name="citizen_land_detail_status_desc1" pre="nowrap" />
            </dt>
            <dd>{format(participationStatus.participantsCount, { zeroAsEmpty: true })}</dd>
            <dt>Citizen Lv{citizenNftLevel}</dt>
            <dd>{format(participationStatus.citizenNftCount, { zeroAsEmpty: true })}</dd>
            <dt>LUP</dt>
            <dd>{format(participationStatus.levelUpPassCount, { zeroAsEmpty: true })}</dd>
          </HorizontalDescriptionList>
        </Card>
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.button}
          disabled={((!!error || isLoading) && isLoggedIn) || isVaultIosDevice}
          onClick={onClick}
          color="primary"
          data-event="click"
          data-event-target="land_join_now_btn">
          {!error || !isLoggedIn ? (
            <Translation name="citizen_join_button" />
          ) : (
            buttonXltByStatus[error]
          )}
        </Button>
      </div>
    </BaseCard>
  );
};
